// custom middleware for optimist redux

// NOTE: redux-optimist docs recommend a general 'status' reducer with a 'writing' flag and a error
// property to hold errors for failed actions. Not being used atm because I don't see the need.

import { BEGIN, COMMIT, REVERT } from "redux-optimist";
import { notificationService, alertService } from "client-app-core";
import { getTranslation } from "orion-components/i18n";
import { createUserFeedback } from "orion-components/Dock/actions";
import EventBus from "orion-components/SharedComponents/EventBus";

let nextTransactionID = 1;
const optimistMiddleware = (store) => (next) => (action) => {
	// Adding a serializable transactionId to every action so optimist can keep track of where it needs to commit/revert
	// This needs to be included in every optimist middleware for redundancy, only the first in the middleware chain will take effect

	if (!action) {
		console.error(
			"optimistMiddleware received an action that was not defined. Action would have been assigned transaction ID: ",
			nextTransactionID
		);
	} else {
		if (!action.transactionId && !action.optimist) {
			action = Object.assign({}, action, {
				transactionId: nextTransactionID++
			});
		}

		if (action.type === "CLOSE_NOTIFICATION") {
			next({
				...action,
				optimist: { type: BEGIN, id: action.transactionId }
			});

			const closeCallback = (err, response) => {
				if (err) {
					console.log(err);
					next({
						type: "CLOSE_NOTIFICATION_FAILED",
						notificationId: action.notificationId,
						notification: action.notification,
						error: err,
						optimist: { type: REVERT, id: action.transactionId }
					});
				} else {
					next({
						type: "CLOSE_NOTIFICATION_COMPLETE",
						notificationId: action.notificationId,
						notification: action.notification,
						response: response,
						optimist: { type: COMMIT, id: action.transactionId }
					});
				}
			};

			if (action.serviceType == "alertService") {
				alertService.closeAlert(action.notificationId, closeCallback);
			} else {
				notificationService.closeNotification(action.notificationId, closeCallback);
			}
		} else if (action.type === "CLOSE_NOTIFICATIONS" || action.type === "CLOSE_ALERTS") {
			const alertIds = action.alerts ? action.alerts.map(a => a.id) : null;

			next({
				...action,
				optimist: { type: BEGIN, id: action.transactionId }
			});

			const closeBulkCallback = (err, response) => {
				if (err) {
					console.log(err);
					next({
						type: "CLOSE_NOTIFICATIONS_FAILED",
						notifications: action.notifications || action.alerts,
						notificationIds: action.notificationIds || alertIds,
						error: err,
						optimist: { type: REVERT, id: action.transactionId }
					});
				} else {
					next({
						type: "CLOSE_NOTIFICATIONS_COMPLETE",
						notifications: action.notifications || action.alerts,
						notificationIds: action.notificationIds || alertIds,
						response: response,
						optimist: { type: COMMIT, id: action.transactionId }
					});

					// Handle partial success - restore alerts that failed validation
					if (response.failedAlertIds && response.failedAlertIds.length > 0 && action.alerts) {
						const failedAlerts = action.alerts.filter(a => response.failedAlertIds.includes(a.id));
						if (failedAlerts.length > 0) {
							// Restore failed alerts to active state
							store.dispatch({ type: "RESTORE_ALERTS", alerts: failedAlerts });

							// Restore failed alerts for other subscribers (e.g. audio player)
							EventBus.publish("reopenAlerts", response.failedAlertIds);

							// Show toast notification
							const message = getTranslation(
								"global.dock.notifications.main.settingsMenu.dismissSomeAlertsFailed"
							);
							store.dispatch(createUserFeedback(message, null, true));
						}
					}
				}
			};

			if (action.serviceType == "alertService") {
				alertService.closeBulkAlerts(alertIds, closeBulkCallback);
			} else {
				notificationService.closeBulkNotifications(action.notificationIds, closeBulkCallback);
			}
		} else if (action.type === "REOPEN_ALERT") {
			next({
				...action,
				optimist: { type: BEGIN, id: action.transactionId }
			});

			const reopenCallback = (err, response) => {
				if (err) {
					console.log(err);
					next({
						type: "REOPEN_ALERT_FAILED",
						notificationId: action.notificationId,
						notification: action.notification,
						error: err,
						optimist: { type: REVERT, id: action.transactionId }
					});
				} else {
					next({
						type: "REOPEN_ALERT_COMPLETE",
						notificationId: action.notificationId,
						notification: action.notification,
						response: response,
						optimist: { type: COMMIT, id: action.transactionId }
					});
				}
			};

			alertService.reopenAlert(action.notificationId, reopenCallback);
		} else if (action.type === "REOPEN_ALERTS") {
			next({
				...action,
				optimist: { type: BEGIN, id: action.transactionId }
			});

			const reopenBulkCallback = (err, response) => {
				if (err) {
					console.log(err);
					next({
						type: "REOPEN_ALERTS_FAILED",
						notifications: action.notifications,
						notificationIds: action.notificationIds,
						error: err,
						optimist: { type: REVERT, id: action.transactionId }
					});
				} else {
					next({
						type: "REOPEN_ALERTS_COMPLETE",
						notifications: action.notifications,
						notificationIds: action.notificationIds,
						response: response,
						optimist: { type: COMMIT, id: action.transactionId }
					});
				}
			};

			alertService.reopenBulkAlerts(action.notificationIds, reopenBulkCallback);
		} else {
			return next(action);
		}
	}
};

export default optimistMiddleware;
